import { useEventCallback, useEventListener } from '@passkeys/hooks';
import { useRef } from 'react';

import { cn } from '@/ui/utils/classnames';
import sprite from './assets/nfts-sprite.png';

const NUMBER_OF_ROWS = 4;
const NUMBER_OF_COLS = 7;
const ITEM_SIZE = 52;

const applyTransform = (
  item: HTMLElement,
  {
    scale,
    x = 0,
    y = 0,
    z,
  }: { scale: number; x?: number; y?: number; z: number },
): void => {
  item.style.setProperty(
    'transform',
    `scale(${scale}) translate(${x}px, ${y}px)`,
  );
  item.style.setProperty('z-index', z.toString());
};

function AnimatedGrid({ className }: { className?: string }) {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleMouseOver = useEventCallback((event: MouseEvent) => {
    const target = event.target as HTMLDivElement;

    const items = Array.from(
      gridRef.current?.children ?? [],
    ) as HTMLDivElement[];

    if (!items.includes(target)) {
      return;
    }

    const currentIndex = items.indexOf(target);

    const hoveredRow = Math.floor(currentIndex / NUMBER_OF_COLS);
    const hoveredCol = currentIndex % NUMBER_OF_COLS;

    items.forEach((item, index) => {
      const itemRow = Math.floor(index / NUMBER_OF_COLS);
      const itemCol = index % NUMBER_OF_COLS;

      if (index === currentIndex) {
        applyTransform(item, { scale: 1.5, z: 5 });
      } else if (hoveredRow === itemRow) {
        const distanceX = Math.abs(hoveredCol - itemCol);
        const translateX = -16 / distanceX;
        applyTransform(item, {
          scale: 0.9,
          x: hoveredCol > itemCol ? translateX : -translateX,
          z: 2,
        });
      } else if (hoveredCol === itemCol) {
        const distanceY = Math.abs(hoveredRow - itemRow);
        const translateY = 16 / distanceY;
        applyTransform(item, {
          scale: 0.9,
          y: hoveredRow > itemRow ? -translateY : translateY,
          z: 2,
        });
      } else if (
        Math.abs(hoveredRow - itemRow) === 1 &&
        Math.abs(hoveredCol - itemCol) === 1
      ) {
        const distanceX = Math.abs(hoveredCol - itemCol);
        const distanceY = Math.abs(hoveredRow - itemRow);
        const translateX = -2 / distanceX;
        const translateY = 2 / distanceY;
        applyTransform(item, {
          scale: 0.85,
          x: hoveredCol > itemCol ? translateX : -translateX,
          y: hoveredRow > itemRow ? -translateY : translateY,
          z: 2,
        });
      } else {
        applyTransform(item, { scale: 1, z: 1 });
      }
    });
  });

  const handleMouseLeave = useEventCallback(() => {
    const items = Array.from(
      gridRef.current?.children ?? [],
    ) as HTMLDivElement[];

    items.forEach((item) => {
      item.style.setProperty('transform', 'scale(1)');
    });
  });

  useEventListener('mouseover', handleMouseOver, gridRef);
  useEventListener('mouseleave', handleMouseLeave, gridRef);

  return (
    <div
      ref={gridRef}
      className={cn('grid grid-cols-7 grid-rows-4 gap-1', className)}
    >
      {Array.from({ length: NUMBER_OF_COLS * NUMBER_OF_ROWS }, (_, index) => (
        <div
          key={index}
          className="aspect-square bg-no-repeat transition-all duration-500 ease-in-out"
          style={{
            width: `${ITEM_SIZE}px`,
            height: `${ITEM_SIZE}px`,
            backgroundImage: `url(${sprite})`,
            backgroundPosition: `-${(index % NUMBER_OF_COLS) * ITEM_SIZE}px -${Math.floor(index / NUMBER_OF_COLS) * ITEM_SIZE}px`,
            backgroundSize: `${NUMBER_OF_COLS * ITEM_SIZE}px ${NUMBER_OF_ROWS * ITEM_SIZE}px`,
          }}
        />
      ))}
    </div>
  );
}

export default AnimatedGrid;
